/**
 * One filter, six views.
 *
 * The whole client hangs off a single `Filter` object. Switching from cards to
 * the timeline does not reset it, because the narrowing is a property of what
 * you are looking at and not of how you are looking at it. Views read
 * `store.filter`, never their own copy, and re-render when it changes.
 *
 * The view name, the filter and the current person or tree root are also
 * mirrored into the URL hash so a narrowed archive is a link somebody can send.
 */

import type { Capability, Filter, Me, Stats } from "./types";

export type ViewName =
  | "cards"
  | "dates"
  | "timeline"
  | "people"
  | "tree"
  | "albums"
  | "dupes"
  | "review"
  | "reels"
  | "reel-editor";

/**
 * The rail's sections, and the ways of looking that live inside each.
 *
 * Two levels, and both earn their place. A section is a different *subject* —
 * the artifacts, the people, the film — and lives in the rail, where it stays
 * put and can be reached with one key. A view is a different way of looking at
 * the subject you are already on, and lives in a dropdown, because those are a
 * list that grows and they all share one filter.
 *
 * Albums sits under Artifacts rather than being a section of its own. An album
 * is not a different archive, it is a hand-made way of looking at the one you
 * have — the same thing the grid, the date buckets and the timeline are.
 *
 * "Artifacts" rather than "Photographs" because an archive holds letters,
 * certificates and film as well, and naming the section after one of them
 * quietly says the others do not belong.
 */
export type SectionName = "artifacts" | "people" | "film";

export interface Section {
  name: SectionName;
  label: string;
  icon: string;
  /** First is the default when the section is opened. */
  views: ViewName[];
}

export const SECTIONS: Section[] = [
  {
    name: "artifacts",
    label: "Artifacts",
    icon: "grid",
    views: ["cards", "dates", "timeline", "albums", "dupes", "review"],
  },
  { name: "people", label: "People", icon: "people", views: ["people", "tree"] },
  { name: "film", label: "Reels", icon: "play", views: ["reels", "reel-editor"] },
];

/** What each view is called in the picker. */
export const VIEW_LABELS: Record<ViewName, string> = {
  cards: "Photographs",
  dates: "By date",
  timeline: "Timeline",
  albums: "Albums",
  dupes: "Duplicates",
  review: "Waiting for you",
  people: "Everyone",
  tree: "Family tree",
  reels: "Reels",
  "reel-editor": "Make a reel",
};

/** The icon shown beside the picker, so the current view reads at a glance. */
export const VIEW_ICONS: Record<ViewName, string> = {
  cards: "grid",
  dates: "calendar",
  timeline: "timeline",
  albums: "album",
  dupes: "album",
  review: "eye",
  people: "people",
  tree: "people",
  reels: "play",
  "reel-editor": "film",
};

export function sectionOf(view: ViewName): Section {
  return SECTIONS.find((s) => s.views.includes(view)) ?? SECTIONS[0]!;
}

export const VIEWS: ViewName[] = SECTIONS.flatMap((s) => s.views);

export interface Route {
  view: ViewName;
  /** Set when the People tab is showing one person rather than the index. */
  personId: string | null;
  /** The identity the tree is drawn around. Null means "the server decides". */
  treeRoot: string | null;
}

export type CardLayout = "gallery" | "masonry" | "list";

export type Change = "filter" | "route" | "library" | "stats" | "named" | "layout";

type Listener = (what: Change) => void;

const isView = (s: string | undefined): s is ViewName =>
  !!s && (VIEWS as string[]).includes(s);

export class Store {
  filter: Filter = {};
  route: Route = { view: "cards", personId: null, treeRoot: null };
  cardLayout: CardLayout = "gallery";
  libraryId = "";
  me: Me | null = null;
  stats: Stats | null = null;

  /**
   * Grain choices live here so they survive a view switch too. Dates open at
   * decade grain: a scanned archive is mostly guessed dates, and a year-grain
   * first screen of an inherited box is thirty buckets holding one photograph.
   */
  dateGrain: "month" | "year" | "decade" = "decade";
  timelineGrain: "year" | "decade" = "year";
  /**
   * Where the timeline's zoom sits, as an index into its own ZOOMS table.
   *
   * Here rather than in the view so that walking away to the grid and coming
   * back does not throw away how far in somebody had chosen to look.
   */
  timelineZoom = 1;
  treeDepth = 4;
  treeDirection: "both" | "up" | "down" = "both";
  treePhotographed = false;

  private listeners = new Set<Listener>();
  private muted = false;

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(what: Change): void {
    if (this.muted) return;
    for (const fn of [...this.listeners]) fn(what);
  }

  // --- filter -------------------------------------------------------------

  setFilter(patch: Partial<Filter>): void {
    const next: Filter = { ...this.filter, ...patch };
    // An absent key and an empty one mean the same thing to the API; keep only
    // the truthy ones so `active` below is a simple emptiness test.
    for (const k of Object.keys(next) as Array<keyof Filter>) {
      const v = next[k];
      if (v === undefined || v === "" || v === false || (Array.isArray(v) && !v.length)) {
        delete next[k];
      }
    }
    if (JSON.stringify(next) === JSON.stringify(this.filter)) return;
    this.filter = next;
    this.sync();
    this.emit("filter");
  }

  clearFilter(): void {
    if (!this.narrowed) return;
    this.filter = {};
    this.sync();
    this.emit("filter");
  }

  togglePerson(id: string): void {
    const people = new Set(this.filter.people ?? []);
    if (people.has(id)) people.delete(id);
    else people.add(id);
    this.setFilter({ people: [...people] });
  }

  setCardLayout(layout: CardLayout): void {
    if (layout === this.cardLayout) return;
    this.cardLayout = layout;
    try {
      localStorage.setItem("heirloom.card-layout", layout);
    } catch {
      /* the default gallery remains available */
    }
    this.emit("layout");
  }

  /** True when anything at all is narrowing the archive. */
  get narrowed(): boolean {
    return Object.keys(this.filter).length > 0;
  }

  /** How the narrowing reads, in words, for the summary line. */
  describe(nameOf: (id: string) => string): string[] {
    const f = this.filter;
    const out: string[] = [];
    if (f.q) out.push(`matching “${f.q}”`);
    for (const p of f.people ?? []) out.push(nameOf(p));
    if (f.from && f.to) out.push(`${f.from} to ${f.to}`);
    else if (f.from) out.push(`from ${f.from}`);
    else if (f.to) out.push(`up to ${f.to}`);
    if (f.kind) out.push(f.kind === "video" ? "film only" : "photographs only");
    if (f.unnamed) out.push("nobody named");
    if (f.withStories) out.push("with a story");
    if (f.albumId) out.push("in one album");
    if (f.includeHidden) out.push("including hidden");
    return out;
  }

  // --- route --------------------------------------------------------------

  /**
   * Somebody just named a person.
   *
   * Its own signal because it has a consequence beyond redrawing: a name is
   * new evidence, so the server offers back every photograph holding a face
   * nobody has placed and something has to go and look at them. The live
   * socket carries the same news, but naming should not depend on a socket
   * being up — the person is sitting there having just typed a name.
   */
  named(): void {
    this.emit("named");
  }

    go(view: ViewName, opts: Partial<Omit<Route, "view">> = {}): void {
    const next: Route = {
      view,
      personId: opts.personId !== undefined ? opts.personId : view === "people" ? this.route.personId : null,
      treeRoot: opts.treeRoot !== undefined ? opts.treeRoot : this.route.treeRoot,
    };
    if (JSON.stringify(next) === JSON.stringify(this.route)) return;
    this.route = next;
    this.sync();
    this.emit("route");
  }

  setLibrary(id: string): void {
    if (id === this.libraryId) return;
    this.libraryId = id;
    this.stats = null;
    try {
      localStorage.setItem("heirloom.library", id);
    } catch {
      /* private browsing; the default library is fine */
    }
    this.sync();
    this.emit("library");
  }

  can(c: Capability): boolean {
    return this.stats?.can.includes(c) ?? false;
  }

  // --- URL ----------------------------------------------------------------

  /**
   * The hash carries the view, the person or root, and the filter. The query
   * string is left alone because `?demo=1` lives there.
   */
  private sync(): void {
    const q = new URLSearchParams();
    const f = this.filter;
    if (f.q) q.set("q", f.q);
    for (const p of f.people ?? []) q.append("person", p);
    if (f.from) q.set("from", f.from);
    if (f.to) q.set("to", f.to);
    if (f.albumId) q.set("album", f.albumId);
    if (f.kind) q.set("kind", f.kind);
    if (f.unnamed) q.set("unnamed", "1");
    if (f.withStories) q.set("stories", "1");
    if (f.includeHidden) q.set("hidden", "1");
    if (this.route.personId) q.set("who", this.route.personId);
    if (this.route.treeRoot) q.set("root", this.route.treeRoot);
    if (this.libraryId) q.set("lib", this.libraryId);
    const s = q.toString();
    const hash = `#/${this.route.view}${s ? `?${s}` : ""}`;
    if (location.hash !== hash) history.replaceState(null, "", hash);
  }

  /** Read the hash back. Returns the library it names, if any. */
  adopt(): string | null {
    const raw = location.hash.replace(/^#\/?/, "");
    const [path, query] = raw.split("?");
    const q = new URLSearchParams(query ?? "");
    this.muted = true;
    if (path === "video") {
      this.route.view = "reels";
      history.replaceState(null, "", `#/reels${query ? `?${query}` : ""}`);
    } else if (isView(path)) this.route.view = path;
    this.route.personId = q.get("who");
    this.route.treeRoot = q.get("root");
    const people = q.getAll("person");
    this.setFilter({
      q: q.get("q") ?? undefined,
      people: people.length ? people : undefined,
      from: q.get("from") ?? undefined,
      to: q.get("to") ?? undefined,
      albumId: q.get("album") ?? undefined,
      kind: (q.get("kind") as Filter["kind"]) ?? undefined,
      unnamed: q.get("unnamed") === "1" || undefined,
      withStories: q.get("stories") === "1" || undefined,
      includeHidden: q.get("hidden") === "1" || undefined,
    });
    this.muted = false;
    return q.get("lib");
  }
}

export const store = new Store();

try {
  const layout = localStorage.getItem("heirloom.card-layout");
  if (layout === "gallery" || layout === "masonry" || layout === "list") {
    store.cardLayout = layout;
  }
} catch {
  /* the default gallery remains available */
}
